import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useCatalog } from "@/providers/CatalogProvider";
import { useStudentQuery } from "@/hooks/useStudents";
import {
  useActivityAssessmentSession,
  type ActivityAssessmentSaveStatus,
  type ObservationSaveStatus
} from "@/hooks/useActivityAssessmentSession";
import type { ActivityDefinition } from "@/types/activity";
import type { AssessmentEntry } from "@/types/assessment";
import type { ScaleEntry } from "@/types/scale";
import { VoiceInputButton } from "@/components/common/VoiceInputButton";
import { AssessmentBreadcrumbs } from "@/components/assessments/AssessmentBreadcrumbs";
import { AssessmentEntryModeSwitch } from "@/components/assessments/AssessmentEntryModeSwitch";
import { AssessmentStartPanel } from "@/components/assessments/AssessmentStartPanel";
import { getDomainByCode, type DomainCode } from "@/constants/domains";
import {
  activityStepRequiredCodes,
  isActivityStepComplete,
  nextUnscoredEntryCode,
  requiredEntryCodes,
} from "@/utils/assessment-progress";
import { assessmentBelongsToStudent } from "@/utils/student-workspace";

interface ActivityStepView {
  id: string;
  title: string;
  description: string;
  entryCodes: string[];
  domainCodeSet: DomainCode[];
}

type ActivityMode = "observe" | "score";

export function ActivityAssessmentPage(): JSX.Element {
  const { activityLibrary, scaleLibrary } = useCatalog();
  const { studentId = "" } = useParams<{ studentId: string }>();
  const [searchParams] = useSearchParams();
  const requestedAssessmentId = searchParams.get("assessmentId") || undefined;

  const [activeActivityCode, setActiveActivityCode] = useState<string | undefined>(undefined);
  const [activeStepId, setActiveStepId] = useState<string | undefined>(undefined);
  const [mode, setMode] = useState<ActivityMode>("score");
  const [modeSwitchError, setModeSwitchError] = useState<string>();
  const [observationTexts, setObservationTexts] = useState<Record<string, string>>({});
  const [pendingScrollCode, setPendingScrollCode] = useState<string>();
  const entryRefs = useRef(new Map<string, HTMLElement>());
  const entriesScrollRef = useRef<HTMLDivElement>(null);

  const session = useActivityAssessmentSession(requestedAssessmentId, studentId);
  const {
    assessment,
    assessmentLoading,
    assessmentError,
    entries,
    entriesLoading,
    observations,
    observationsLoading,
    observationSaveStatus,
    saveObservation,
    retryObservation,
    reloadObservation,
    flushObservations,
    saveEntry,
    flushEntries,
    saveStatus,
    markStatus,
    marking
  } = session;
  const studentQuery = useStudentQuery(studentId);

  const activityList = activityLibrary.activities;

  useEffect(() => {
    if (!activeActivityCode && activityList.length > 0) {
      setActiveActivityCode(activityList[0].code);
    }
  }, [activeActivityCode, activityList]);

  const activeActivity = useMemo<ActivityDefinition | undefined>(() => {
    if (!activeActivityCode) {
      return undefined;
    }
    return activityLibrary.activityIndex.get(activeActivityCode);
  }, [activityLibrary.activityIndex, activeActivityCode]);

  const activitySteps = useMemo<ActivityStepView[]>(() => {
    if (!activeActivity) return [];
    return activeActivity.modules.flatMap((module, moduleIndex) =>
      module.steps.map((step, stepIndex) => ({
        id: step.id ?? `${activeActivity.code}-${moduleIndex}-${stepIndex}`,
        title: step.title,
        description: step.description,
        entryCodes: step.entryCodes,
        domainCodeSet: step.domainCodeSet
      })),
    );
  }, [activeActivity]);

  useEffect(() => {
    if (!activitySteps.some((step) => step.id === activeStepId)) {
      setActiveStepId(activitySteps[0]?.id);
    }
  }, [activeStepId, activitySteps]);

  const activeStep = useMemo<ActivityStepView | undefined>(() => {
    return activitySteps.find((step) => step.id === activeStepId);
  }, [activitySteps, activeStepId]);

  const activeObservationKey =
    activeActivityCode && activeStepId
      ? buildObservationKey(activeActivityCode, activeStepId)
      : undefined;

  useEffect(() => {
    setObservationTexts({});
    setMode("score");
    setModeSwitchError(undefined);
    setPendingScrollCode(undefined);
  }, [requestedAssessmentId]);

  useEffect(() => {
    setObservationTexts((previous) => {
      const next = { ...previous };
      observations.forEach((observation) => {
        const key = buildObservationKey(observation.activityCode, observation.stepId);
        const status = observationSaveStatus[key];
        if (
          next[key] === undefined ||
          status === undefined ||
          status === "idle" ||
          status === "saved"
        ) {
          next[key] = observation.text;
        }
      });
      return next;
    });
  }, [observationSaveStatus, observations]);

  const activeStepIndex = useMemo(() => {
    return activitySteps.findIndex((step) => step.id === activeStepId);
  }, [activitySteps, activeStepId]);

  const canNavigatePrev = activeStepIndex > 0;
  const canNavigateNext =
    activeStepIndex >= 0 && activeStepIndex < activitySteps.length - 1;

  const navigateStep = useCallback(
    (direction: "prev" | "next") => {
      if (activitySteps.length === 0 || activeStepIndex < 0) {
        return;
      }
      const nextIndex =
        direction === "prev" ? activeStepIndex - 1 : activeStepIndex + 1;
      if (nextIndex < 0 || nextIndex >= activitySteps.length) {
        return;
      }
      const nextStep = activitySteps[nextIndex];
      setActiveStepId(nextStep.id);
    },
    [activitySteps, activeStepIndex],
  );

  const entriesMap = useMemo(() => {
    const map = new Map<string, AssessmentEntry>();
    entries.forEach((entry) => map.set(entry.entryCode, entry));
    return map;
  }, [entries]);

  const preparation = useMemo(() => {
    if (!activeActivity) return undefined;
    const materials = Array.from(
      new Set(activeActivity.modules.flatMap((module) => module.materials).filter(Boolean)),
    );
    const domains = Array.from(
      new Set(
        activeActivity.modules.flatMap((module) =>
          module.steps.flatMap((step) => step.domainCodeSet),
        ),
      ),
    ).map((code) => getDomainByCode(code).name);
    return {
      materials,
      domains,
      stepCount: activitySteps.length
    };
  }, [activeActivity, activitySteps.length]);

  const rawEntries = (activeStep?.entryCodes ?? []).map((code) =>
    scaleLibrary.entryIndex.get(code),
  );
  const missingEntries = rawEntries.some((entry) => !entry);
  const activeRequiredCodes = useMemo(
    () =>
      activityStepRequiredCodes(activeStep?.entryCodes ?? [], scaleLibrary, entries),
    [activeStep?.entryCodes, entries, scaleLibrary],
  );
  const activeEntries = activeRequiredCodes
    .map((code) => scaleLibrary.entryIndex.get(code))
    .filter((entry): entry is ScaleEntry => Boolean(entry));

  const eventsIndicator = getSaveIndicator(saveStatus);

  const activityCompletion = useMemo(() => {
    const map = new Map<string, { completedSteps: number; totalSteps: number }>();
    activityLibrary.activities.forEach((activity) => {
      const steps = activity.modules.flatMap((module) => module.steps);
      const completed = steps.filter((step) =>
        isActivityStepComplete(step.entryCodes, scaleLibrary, entries),
      ).length;
      map.set(activity.code, { completedSteps: completed, totalSteps: steps.length });
    });
    return map;
  }, [activityLibrary.activities, entries, scaleLibrary]);

  const requiredCodes = useMemo(
    () => requiredEntryCodes(scaleLibrary, entries),
    [entries, scaleLibrary],
  );
  const allRequiredScored =
    requiredCodes.length > 0 &&
    requiredCodes.every((code) => {
      const score = entriesMap.get(code)?.score;
      return score !== null && score !== undefined;
    });

  const registerEntryRef = useCallback((entryCode: string, node: HTMLElement | null) => {
    if (node) entryRefs.current.set(entryCode, node);
    else entryRefs.current.delete(entryCode);
  }, []);

  useEffect(() => {
    if (!pendingScrollCode) return;
    let secondFrame = 0;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        const container = entriesScrollRef.current;
        const target = entryRefs.current.get(pendingScrollCode);
        if (container && target) scrollInsideContainer(container, target);
        setPendingScrollCode(undefined);
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame) cancelAnimationFrame(secondFrame);
    };
  }, [activeEntries, pendingScrollCode]);

  const handleScoreChange = (entryCode: string, score: 0 | 1 | 2) => {
    if (!assessment || assessment.status === "completed") return;
    const existing = entriesMap.get(entryCode);
    const wasUnscored = existing?.score === null || existing?.score === undefined;
    const entry = scaleLibrary.entryIndex.get(entryCode);
    const projectedEntries = projectEntryScore(entries, assessment.id, entryCode, score);
    void saveEntry({ entryCode, score })
      .then(() => {
        if (!wasUnscored || !activeStep || !entry) return;
        const projectedCodes = activityStepRequiredCodes(
          activeStep.entryCodes,
          scaleLibrary,
          projectedEntries,
        );
        if (entry.level === "long_term" && (score === 0 || score === 1)) {
          const firstChild = scaleLibrary.entries.find(
            (candidate) =>
              candidate.parentCode === entryCode && projectedCodes.includes(candidate.code),
          );
          setPendingScrollCode(firstChild?.code);
          return;
        }
        setPendingScrollCode(
          nextUnscoredEntryCode(projectedCodes, projectedEntries, entryCode),
        );
      })
      .catch(() => undefined);
  };

  const handleCommentChange = (entryCode: string, comment: string) => {
    if (!assessment || assessment.status === "completed") return;
    saveEntry({ entryCode, comment });
  };

  const handleObservationChange = (text: string) => {
    if (!assessment || !activeActivity || !activeStep || assessment.status === "completed") {
      return;
    }
    const key = buildObservationKey(activeActivity.code, activeStep.id);
    setObservationTexts((previous) => ({ ...previous, [key]: text }));
    saveObservation({
      activityCode: activeActivity.code,
      stepId: activeStep.id,
      text
    });
  };

  const handleSubmitAssessment = async () => {
    if (!assessment || assessment.status === "completed") return;
    await markStatus("completed");
  };

  const saveDisabled = !assessment || assessment.status === "completed";

  const sessionReady = assessmentBelongsToStudent(assessment, studentId, "activity");

  const handleModeChange = async (nextMode: ActivityMode) => {
    if (nextMode === mode) return;
    setModeSwitchError(undefined);
    try {
      if (mode === "observe") await flushObservations();
      else await flushEntries();
      setMode(nextMode);
    } catch (error) {
      setModeSwitchError(error instanceof Error ? error.message : "保存失败，请重试");
    }
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm xl:flex-row xl:items-center xl:justify-between">
        <div>
          <AssessmentBreadcrumbs
            currentPage="活动评估"
            studentName={studentQuery.data?.profile.name}
            studentId={studentId}
          />
          <h1 className="text-2xl font-semibold text-slate-800">活动评估</h1>
          <p className="mt-1 text-sm text-slate-500">按活动步骤观察或评分，修改自动保存。</p>
        </div>
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
          {assessment && sessionReady ? (
            <AssessmentEntryModeSwitch
              assessment={assessment}
              studentId={studentId}
              currentMode="activity"
              beforeSwitch={async () => {
                await flushObservations();
                await flushEntries();
              }}
            />
          ) : null}
          {eventsIndicator.label ? (
            <span className={`text-xs ${eventsIndicator.className}`}>
              {eventsIndicator.label}
            </span>
          ) : null}
          <button
            type="button"
            onClick={handleSubmitAssessment}
            disabled={!allRequiredScored || saveDisabled || marking}
            className="rounded-md bg-brand px-4 py-2 text-sm font-semibold text-white shadow hover:bg-brand-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {assessment?.status === "completed" ? "评估已提交" : marking ? "提交中…" : "生成完整报告"}
          </button>
        </div>
      </header>

      {!requestedAssessmentId ? (
        <AssessmentStartPanel studentId={studentId} />
      ) : assessmentLoading ? (
        <section className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500 shadow-sm">
          正在读取活动评估数据…
        </section>
      ) : assessmentError ? (
        <AssessmentStartPanel
          studentId={studentId}
          title="无法读取这份评估"
          description="评估记录不存在、无权访问或暂时无法加载。系统没有创建替代记录，请返回后重新选择。"
        />
      ) : !sessionReady ? (
        <AssessmentStartPanel
          studentId={studentId}
          title="儿童与评估记录不匹配"
          description="这份评估不属于当前儿童。为保护数据，页面不会显示或修改该记录，请返回当前儿童的评估列表重新选择。"
        />
      ) : (
        <>
          <section
            aria-label="选择活动"
            className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4 min-[1440px]:grid-cols-7"
          >
            {activityList.map((activity) => {
              const progress = activityCompletion.get(activity.code) ?? {
                completedSteps: 0,
                totalSteps: activity.modules.reduce((acc, module) => acc + module.steps.length, 0)
              };
              const percent =
                progress.totalSteps > 0
                  ? Math.round((progress.completedSteps / progress.totalSteps) * 100)
                  : 0;
              const isActive = activity.code === activeActivityCode;
              return (
                <button
                  key={activity.code}
                  type="button"
                  onClick={() => {
                    setActiveActivityCode(activity.code);
                    const steps = activity.modules.flatMap((module) => module.steps);
                    if (steps[0]) {
                      setActiveStepId(steps[0].id ?? `${activity.code}-0-0`);
                    }
                  }}
                  aria-pressed={isActive}
                  className={`flex min-h-16 w-full flex-col justify-between rounded-lg border px-4 py-3 text-left shadow-sm transition ${
                    isActive
                      ? "border-brand bg-brand/10 text-brand"
                      : "border-slate-200 bg-white hover:border-brand/60"
                  }`}
                >
                  <span className="text-sm font-semibold leading-5">{activity.name}</span>
                  <span className="mt-1.5 text-xs text-slate-500">
                    步骤 {progress.completedSteps}/{progress.totalSteps} · 完成度 {percent}%
                  </span>
                </button>
              );
            })}
          </section>

          {activeActivity && preparation ? (
            <section
              aria-label="活动信息"
              className="rounded-xl border border-brand/20 bg-white p-5 shadow-sm"
            >
              <h2 className="text-lg font-semibold text-slate-800">{activeActivity.name}</h2>
              <dl className="mt-4 grid gap-3 text-sm sm:grid-cols-2 xl:grid-cols-4">
                <div className="min-w-0 rounded-lg bg-slate-50 p-4">
                  <dt className="text-xs font-semibold text-slate-500">记录方式</dt>
                  <dd className="mt-2">
                  <div
                    className="inline-flex self-start rounded-lg border border-slate-200 bg-white p-1"
                    aria-label="活动评估模式"
                  >
                    <button
                      type="button"
                      onClick={() => void handleModeChange("score")}
                      aria-pressed={mode === "score"}
                      className={`rounded-md px-4 py-2 text-sm font-semibold transition ${
                        mode === "score"
                          ? "bg-brand text-white shadow-sm"
                          : "text-slate-600 hover:text-brand-dark"
                      }`}
                    >
                      评分模式
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleModeChange("observe")}
                      aria-pressed={mode === "observe"}
                      className={`rounded-md px-4 py-2 text-sm font-semibold transition ${
                        mode === "observe"
                          ? "bg-brand text-white shadow-sm"
                          : "text-slate-600 hover:text-brand-dark"
                      }`}
                    >
                      观察模式
                    </button>
                  </div>
                  {modeSwitchError ? (
                    <span className="mt-2 block text-xs text-red-600" role="alert">
                      {modeSwitchError}
                    </span>
                  ) : null}
                  </dd>
                </div>
                <div className="min-w-0 rounded-lg bg-slate-50 p-4">
                  <dt className="text-xs font-semibold text-slate-500">步骤</dt>
                  <dd className="mt-2 font-medium text-slate-800">
                    {preparation.stepCount} 个真实步骤
                  </dd>
                </div>
                <div className="min-w-0 rounded-lg bg-slate-50 p-4">
                  <dt className="text-xs font-semibold text-slate-500">准备材料</dt>
                  <dd className="mt-2 break-words font-medium text-slate-800">
                    {preparation.materials.length > 0 ? preparation.materials.join("、") : "无"}
                  </dd>
                </div>
                <div className="min-w-0 rounded-lg bg-slate-50 p-4">
                  <dt className="text-xs font-semibold text-slate-500">关联领域</dt>
                  <dd className="mt-2 break-words font-medium text-slate-800">
                    {preparation.domains.length > 0 ? preparation.domains.join("、") : "无"}
                  </dd>
                </div>
              </dl>
            </section>
          ) : null}

          <section className="grid items-start gap-4 xl:grid-cols-[minmax(0,3fr)_minmax(24rem,2fr)]">
            <article
              data-testid="activity-context"
              className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm xl:sticky xl:top-4 xl:flex xl:flex-col xl:self-start"
            >
              <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    当前活动
                  </p>
                  <h2 className="text-lg font-semibold text-slate-800">
                    {activeActivity?.name ?? "请选择活动"}
                  </h2>
                  <p className="mt-1 text-xs text-slate-500">
                    通过左右翻页切换步骤，保持同一活动与步骤上下文。
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigateStep("prev")}
                    disabled={!canNavigatePrev}
                    className="rounded-full border border-slate-200 p-2 text-sm text-slate-600 hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:border-slate-100 disabled:text-slate-300"
                  >
                    上一步
                  </button>
                  <button
                    type="button"
                    onClick={() => navigateStep("next")}
                    disabled={!canNavigateNext}
                    className="rounded-full border border-slate-200 p-2 text-sm text-slate-600 hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:border-slate-100 disabled:text-slate-300"
                  >
                    下一步
                  </button>
                </div>
              </div>

              <div className="mt-6 space-y-4 flex-1">
                {activeStep ? (
                  <>
                    <div className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-3 text-sm">
                      <div>
                        步骤 {activeStepIndex + 1}/{activitySteps.length}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {activitySteps.map((step, index) => {
                          const completed = isActivityStepComplete(
                            step.entryCodes,
                            scaleLibrary,
                            entries,
                          );
                          return (
                            <button
                              key={step.id}
                              type="button"
                              onClick={() => setActiveStepId(step.id)}
                              className={`rounded-full px-3 py-1 text-xs ${
                                step.id === activeStepId
                                  ? "bg-brand text-white"
                                  : "bg-white text-slate-600"
                              }`}
                            >
                              {index + 1} · {completed ? "完成" : "未评"}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/60 p-5">
                      <StepDescription text={activeStep.description} />
                    </div>
                  </>
                ) : (
                  <p className="text-sm text-slate-500">请选择一个活动步骤开始记录。</p>
                )}
              </div>
            </article>

            <section className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm xl:h-[calc(100dvh-6rem)] xl:max-h-[52rem] xl:min-h-[34rem]">
              <div className="border-b border-slate-200 px-6 py-4">
                <h3 className="text-sm font-semibold text-slate-800">
                  {mode === "observe" ? "当前步骤观察" : "量表条目（自动同步）"}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  {mode === "observe"
                    ? "先专注记录儿童的自然表现，需要时再切到评分模式。"
                    : "当前步骤关联的量表条目在此评分，下方可滚动查看更多。"}
                </p>
              </div>
              <div
                ref={entriesScrollRef}
                data-testid="activity-entry-scroll"
                className="space-y-4 px-6 py-4 xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:overscroll-contain"
              >
                {activeStep && activeActivity ? (
                  mode === "observe" ? (
                    observationsLoading ? (
                      <p className="text-sm text-slate-500">正在读取观察记录…</p>
                    ) : (
                      <ObservationEditor
                        value={observationTexts[activeObservationKey ?? ""] ?? ""}
                        status={observationSaveStatus[activeObservationKey ?? ""] ?? "idle"}
                        disabled={saveDisabled}
                        onChange={handleObservationChange}
                        onRetry={() =>
                          void retryObservation(activeActivity.code, activeStep.id).catch(
                            () => undefined,
                          )
                        }
                        onCopy={() =>
                          void copyText(observationTexts[activeObservationKey ?? ""] ?? "")
                        }
                        onReload={() =>
                          void reloadObservation(activeActivity.code, activeStep.id).catch(
                            () => undefined,
                          )
                        }
                      />
                    )
                  ) : entriesLoading ? (
                    <p className="text-sm text-slate-500">正在加载步骤条目…</p>
                  ) : missingEntries ? (
                    <p className="text-sm text-red-500">
                      某些条目在量表中未找到，请核对活动配置。
                    </p>
                  ) : (
                    <>
                      {(observationTexts[activeObservationKey ?? ""] ?? "").trim() ? (
                        <div className="rounded-lg border border-brand/20 bg-brand/5 p-4">
                          <div className="flex items-center justify-between gap-3">
                            <p className="text-xs font-semibold text-brand-dark">本步骤观察摘要</p>
                            <button
                              type="button"
                              onClick={() => setMode("observe")}
                              className="text-xs font-semibold text-brand hover:underline"
                            >
                              返回观察模式编辑
                            </button>
                          </div>
                          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700">
                            {observationTexts[activeObservationKey ?? ""]}
                          </p>
                        </div>
                      ) : null}
                      {activeEntries.map((entry) => {
                        const record = entriesMap.get(entry.code);
                        return (
                          <ActivityEntryCard
                            key={entry.code}
                            entry={entry}
                            record={record}
                            onScore={handleScoreChange}
                            onComment={handleCommentChange}
                            registerRef={registerEntryRef}
                            disabled={saveDisabled}
                          />
                        );
                      })}
                    </>
                  )
                ) : (
                  <p className="text-sm text-slate-500">请先选择左侧步骤。</p>
                )}
              </div>
            </section>
          </section>
        </>
      )}
    </div>
  );
}

function ActivityEntryCard({
  entry,
  record,
  onScore,
  onComment,
  registerRef,
  disabled
}: {
  entry: ScaleEntry;
  record?: AssessmentEntry;
  onScore: (entryCode: string, score: 0 | 1 | 2) => void;
  onComment: (entryCode: string, comment: string) => void;
  registerRef: (entryCode: string, node: HTMLElement | null) => void;
  disabled: boolean;
}): JSX.Element {
  const [comment, setComment] = useState(record?.comment ?? "");

  useEffect(() => {
    setComment(record?.comment ?? "");
  }, [record?.comment, entry.code]);

  useEffect(() => {
    if (disabled) return;
    if (comment === (record?.comment ?? "")) {
      return;
    }
    const timer = setTimeout(() => {
      onComment(entry.code, comment);
    }, 600);
    return () => clearTimeout(timer);
  }, [comment, disabled, onComment, record?.comment, entry.code]);

  const handleScoreClick = (score: 0 | 1 | 2) => {
    onScore(entry.code, score);
  };

  return (
    <article
      id={`activity-entry-${entry.code}`}
      ref={(node) => registerRef(entry.code, node)}
      className="rounded-lg border border-slate-200 bg-slate-50 p-4 shadow-sm"
    >
      <div className="flex flex-col gap-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-slate-800">
            {entry.code} · {entry.shortTermGoal?.title ?? entry.longTermGoal.title}
          </h3>
        </div>
        <ActivityScoreSelector
          score={record?.score}
          disabled={disabled}
          onSelect={handleScoreClick}
        />
        <div className="text-sm leading-7 text-slate-600">
          <p>{entry.standard}</p>
          {entry.examples ? (
            <p className="mt-2 text-xs text-slate-500">示例：{entry.examples}</p>
          ) : null}
        </div>
      </div>
      <div className="mt-4">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">备注</div>
        <div className="relative mt-1">
          <textarea
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            onBlur={() => {
              if (comment !== (record?.comment ?? "")) onComment(entry.code, comment);
            }}
            rows={3}
            disabled={disabled}
            className="w-full rounded border border-slate-300 px-3 py-2 pr-12 text-sm text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 disabled:cursor-not-allowed disabled:bg-slate-100"
            placeholder="记录观察到的行为或补充说明（自动保存）"
          />
          {!disabled ? (
            <VoiceInputButton
              size="sm"
              className="absolute bottom-2 right-2"
              initialValue={comment}
              onSave={setComment}
            />
          ) : null}
        </div>
      </div>
    </article>
  );
}

function ObservationEditor({
  value,
  status,
  disabled,
  onChange,
  onRetry,
  onCopy,
  onReload
}: {
  value: string;
  status: ObservationSaveStatus;
  disabled: boolean;
  onChange: (value: string) => void;
  onRetry: () => void;
  onCopy: () => void;
  onReload: () => void;
}): JSX.Element {
  const indicator = getObservationIndicator(status);
  return (
    <div className="rounded-xl border border-brand/20 bg-brand/5 p-4">
      <div className="flex items-center justify-between gap-3">
        <label htmlFor="activity-observation" className="text-sm font-semibold text-slate-800">
          观察记录
        </label>
        <div className="flex items-center gap-2" aria-live="polite">
          {indicator.label ? (
            <span className={`text-xs ${indicator.className}`}>{indicator.label}</span>
          ) : null}
          {status === "error" || status === "conflict" ? (
            <button
              type="button"
              onClick={onRetry}
              className="text-xs font-semibold text-brand hover:underline"
            >
              重试
            </button>
          ) : null}
          {status === "conflict" ? (
            <>
              <button
                type="button"
                onClick={onCopy}
                className="text-xs font-semibold text-brand hover:underline"
              >
                复制当前内容
              </button>
              <button
                type="button"
                onClick={onReload}
                className="text-xs font-semibold text-brand hover:underline"
              >
                重新载入服务器版本
              </button>
            </>
          ) : null}
        </div>
      </div>
      <div className="relative mt-3">
        <textarea
          id="activity-observation"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          rows={12}
          maxLength={10_000}
          disabled={disabled}
          className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 pr-14 text-base leading-7 text-slate-700 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20 disabled:cursor-not-allowed disabled:bg-slate-100"
          placeholder="记录儿童在当前活动步骤中的自然表现。可直接输入，也可使用语音转文字。"
        />
        {!disabled ? (
          <VoiceInputButton
            size="sm"
            initialValue={value}
            onSave={onChange}
            className="absolute bottom-3 right-3"
          />
        ) : null}
      </div>
      <p className="mt-2 text-xs text-slate-500">
        {value.length}/10000 · 观察文字独立保存，不会自动换算为分数。
      </p>
    </div>
  );
}

function ActivityScoreSelector({
  score,
  disabled,
  onSelect
}: {
  score?: number | null;
  disabled: boolean;
  onSelect: (value: 0 | 1 | 2) => void;
}): JSX.Element {
  return (
    <div className="w-full rounded-xl border border-brand/30 bg-white px-4 py-2 shadow-sm xl:w-[260px]">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-semibold text-brand whitespace-nowrap">条目得分</span>
        <div className="flex items-center gap-2">
          {[0, 1, 2].map((value) => {
            const option = value as 0 | 1 | 2;
            const isActive = score === option;
            return (
              <button
                key={option}
                type="button"
                disabled={disabled}
                onClick={() => onSelect(option)}
                className={`h-9 w-9 rounded-full border text-sm font-semibold transition ${
                  isActive
                    ? "border-brand bg-brand text-white"
                    : "border-slate-300 bg-white text-slate-600 hover:border-brand hover:text-brand"
                } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
              >
                {option}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function getSaveIndicator(status: ActivityAssessmentSaveStatus) {
  switch (status) {
    case "saving":
      return { label: "保存中…", className: "text-sky-700" };
    case "saved":
      return { label: "最新修改已保存", className: "text-brand-dark" };
    case "error":
      return { label: "保存失败，请重试", className: "text-red-600" };
    default:
      return { label: "", className: "text-slate-500" };
  }
}

function getObservationIndicator(status: ObservationSaveStatus) {
  switch (status) {
    case "dirty":
      return { label: "有更改尚未保存", className: "text-amber-700" };
    case "saving":
      return { label: "正在保存…", className: "text-sky-700" };
    case "saved":
      return { label: "已保存", className: "text-brand-dark" };
    case "error":
      return { label: "保存失败", className: "text-red-600" };
    case "conflict":
      return {
        label: "其他位置已更新，当前内容尚未保存",
        className: "text-red-600"
      };
    default:
      return { label: "", className: "text-slate-500" };
  }
}

function buildObservationKey(activityCode: string, stepId: string): string {
  return `${activityCode}:${stepId}`;
}

async function copyText(value: string): Promise<void> {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(value);
  }
}

function StepDescription({ text }: { text?: string }) {
  const content = text && text.trim().length > 0 ? text : "请按照步骤要求观察并记录。";
  const lines = content.split(/\r?\n/);
  const showMarkers = lines.length > 1;
  return (
    <div className="space-y-2 text-base leading-7 tracking-wide text-slate-700">
      {lines.map((line, index) => (
        <div
          key={`${index}-${line}`}
          className={`flex gap-2 ${showMarkers ? "items-start" : ""}`}
        >
          {showMarkers ? (
            <span
              aria-hidden
              className="mt-2.5 h-2 w-2 flex-shrink-0 rounded-full bg-brand/50"
            />
          ) : null}
          <span className="flex-1 whitespace-pre-wrap">{line || " "}</span>
        </div>
      ))}
    </div>
  );
}

function projectEntryScore(
  entries: readonly AssessmentEntry[],
  assessmentId: string,
  entryCode: string,
  score: 0 | 1 | 2,
): AssessmentEntry[] {
  const existing = entries.find((entry) => entry.entryCode === entryCode);
  if (existing) {
    return entries.map((entry) => (entry.entryCode === entryCode ? { ...entry, score } : entry));
  }
  return [
    ...entries,
    {
      assessmentId,
      entryCode,
      score,
      version: 0,
      updatedAt: "",
    },
  ];
}

function scrollInsideContainer(container: HTMLElement, target: HTMLElement): void {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const containerRect = container.getBoundingClientRect();
  const targetRect = target.getBoundingClientRect();
  container.scrollTo({
    top: container.scrollTop + targetRect.top - containerRect.top - 16,
    behavior: reducedMotion ? "auto" : "smooth",
  });
}
